/**
 * Unit maths shared by every mastersheet view. The portfolio ("All Properties")
 * table and a single property's sheet both feed the summary tiles from rows of one
 * shape: { unit_no, tenant, basic_rent, target_rent, lease_end, sd, sqft }.
 *
 * Inactive properties never reach these helpers: the server scopes both the
 * portfolio and the property dropdown to `status: "Active"`.
 */

export const money = (n) =>
  n === null || n === undefined
    ? "—"
    : new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(n) || 0);

/** Compact money for the summary tiles — $1.2M / $48.3k / $920. */
export const moneyShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
};

export const DAY_MS = 86400000;

/**
 * Whole days from today to `v`; null when there is no usable date. `asOf`
 * (YYYY-MM-DD) measures from that day instead — the Historic sheet's chosen date.
 */
export const daysUntil = (v, asOf) => {
  if (!v) return null;
  const t = new Date(String(v).slice(0, 10)).getTime();
  if (!Number.isFinite(t)) return null;
  const today = asOf ? new Date(String(asOf).slice(0, 10)) : new Date();
  if (!Number.isFinite(today.getTime())) return null;
  if (!asOf) today.setHours(0, 0, 0, 0);
  return Math.round((t - today.getTime()) / DAY_MS);
};

/**
 * The same three variance bands the per-property sheet paints: at/above target,
 * short by under $75, short by $75 or more.
 */
export const varianceOf = (row) => {
  const basic = Number(row.basic_rent);
  const target = Number(row.target_rent);
  if (!Number.isFinite(basic) || !Number.isFinite(target) || (!basic && !target)) {
    return { diff: null, band: null };
  }
  const diff = target - basic;
  if (diff <= 0) return { diff, band: "up", cls: "pft-var--up", indicator: "▲", label: `+${money(Math.abs(diff))}` };
  if (diff < 75) return { diff, band: "near", cls: "pft-var--near", indicator: "~", label: `-${money(diff)}` };
  return { diff, band: "down", cls: "pft-var--down", indicator: "▼", label: `-${money(diff)}` };
};

/** A tenant cell that means nobody: blank, a placeholder token, or the word itself. */
const VACANT_TOKENS = new Set(["", "n/a", "na", "-", "--", "—", "vacant", "empty", "none", "null", "undefined"]);

export const isOccupied = (r) => !VACANT_TOKENS.has(String(r?.tenant || "").trim().toLowerCase());

/** Numeric-aware unit order: 7, 101, 102, 501, 1501 — never 1501 ahead of 501. */
export const compareUnitNo = (a, b) =>
  String(a ?? "").trim().localeCompare(String(b ?? "").trim(), undefined, { numeric: true, sensitivity: "base" });

/** "50" matches 501 and 502 but not 1501: a plain prefix on the unit number only. */
export const unitMatchesPrefix = (unitNo, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return String(unitNo ?? "").trim().toLowerCase().startsWith(q);
};

/**
 * Tenant text from a sheet item: a string, or an array of names / person objects.
 * "N/A" placeholders count as nobody.
 */
export const tenantText = (value) => {
  if (!value) return "";
  const names = (Array.isArray(value) ? value : [value])
    .map((t) => {
      if (typeof t === "string") return t.trim();
      if (t && typeof t === "object") return String(t.name || `${t.firstName || ""} ${t.lastName || ""}`).trim();
      return "";
    })
    .filter((n) => n && !VACANT_TOKENS.has(n.toLowerCase()));
  return names.join(", ");
};

/** A single property's sheet items (server field names) → the row shape the tiles read. */
export const unitRowsFromItems = (items) =>
  (items || []).map((item) => ({
    unit_no: item.unitNo,
    tenant: tenantText(item.tenants || item.tenant),
    basic_rent: item.basicRent,
    target_rent: item.targetRent,
    lease_end: item.leaseEndDmy,
  }));

/**
 * Everything the tiles and the totals row show, over exactly the rows given.
 *
 * Target rent and square feet describe the building, so every unit counts.
 * Basic rent, the deposit, the variance bands and "leases ending" describe the
 * people in it: a vacant unit contributes nothing, whatever stale figures its
 * row still carries from the last tenancy.
 */
export const summarizeUnits = (rows, asOf) => {
  const t = { units: rows.length, occupied: 0, basic: 0, target: 0, sd: 0, sqft: 0, up: 0, near: 0, down: 0, expiring: 0, rentedVacant: 0, rentedExpiring: 0 };
  // "Already rented" needs the portfolio's `signed_lease` flag; rows without it (a single
  // sheet's items) leave `hasRentedInfo` false and the tile is not shown.
  t.hasRentedInfo = rows.some((r) => typeof r.signed_lease === "boolean");
  for (const r of rows) {
    const occupied = isOccupied(r);
    t.target += Number(r.target_rent) || 0;
    t.sqft += Number(r.sqft) || 0;
    if (!occupied) {
      if (r.signed_lease === true) t.rentedVacant += 1;
      continue;
    }
    t.occupied += 1;
    t.basic += Number(r.basic_rent) || 0;
    t.sd += Number(r.sd) || 0;
    const { band } = varianceOf(r);
    if (band === "up") t.up += 1;
    else if (band === "near") t.near += 1;
    else if (band === "down") t.down += 1;
    const d = daysUntil(r.lease_end, asOf);
    if (d !== null && d >= 0 && d <= 60) {
      t.expiring += 1;
      if (r.signed_lease === true) t.rentedExpiring += 1;
    }
  }
  t.rented = t.rentedVacant + t.rentedExpiring;
  t.vacant = t.units - t.occupied;
  t.gap = t.target - t.basic;
  t.occupancy = t.units ? Math.round((t.occupied / t.units) * 100) : 0;
  return t;
};
